import CloseIcon from '@mui/icons-material/Close'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import noop from 'lodash/noop'
import pick from 'lodash/pick'
import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useGetTagsMetadataQuery } from 'api/tags/getTagsMetadata'
import { usePutUpdateTagsMutation } from 'api/tags/putUpdateTags'
import CancelButton from 'components/buttons/CancelButton'
import { getActiveSelectedTags, buildPreservedInactiveTagIdsByType, mergePreservedInactiveIds } from 'components/tags/editTagsPopupHelpers'
import TagsSelect from 'components/tags/TagsSelect'
import { getSelectedTagIds } from 'data/tags/selectedTagsSelector'
import { getEnabledTypes } from 'data/tags/tagsDataMappingSelector'
import { getAccountTags, getLocationTags, getServiceTags } from 'data/tags/tagsMetadataSelector'
import { TAG_TYPE_ACCOUNT, TAG_TYPE_LOCATION, TAG_TYPE_SERVICE, selectTagIds, resetSelectedTagIds } from 'slices/tags/tagsSlice'
import T from 'T'
import { handleError } from 'utils/error'

import type { ButtonProps } from '@mui/material/Button'
import type { SelectedTagsByType, SelectedTagsGroup, TagGroupType, TagMetadata, UpdateTagsPayload } from 'types/modules/tags'

interface EditTagsPopupProps {
  /** Required by the built-in PUT /tags path and its cache invalidation (see api/tags/putUpdateTags.js). */
  accountId: string
  name?: string
  open?: boolean
  disableAccount?: boolean
  disableService?: boolean
  disableLocation?: boolean
  onClose?: () => void
  onSave?: () => void
  defaultSelectedTags?: SelectedTagsByType
  /** When provided, replaces the built-in PUT /tags call — the consumer owns persistence. */
  onConfirm?: (payload: UpdateTagsPayload) => void | Promise<unknown>
  SaveButtonProps?: ButtonProps
}

const EditTagsPopup = ({
  accountId,
  name = '',
  open = false,
  disableAccount = false,
  disableService = false,
  disableLocation = false,
  onClose = noop,
  onSave = noop,
  defaultSelectedTags = {},
  onConfirm,
  SaveButtonProps = {},
}: EditTagsPopupProps) => {
  const dispatch = useDispatch()
  const { isLoading, isFetching } = useGetTagsMetadataQuery(undefined)
  const [putUpdateTags, { isLoading: isUpdateTagsLoading }] = usePutUpdateTagsMutation()

  const allAccountTags: TagMetadata[] = useSelector(getAccountTags)
  const allLocationTags: TagMetadata[] = useSelector(getLocationTags)
  const allServiceTags: TagMetadata[] = useSelector(getServiceTags)

  const selectedTagsUnsaved: UpdateTagsPayload = useSelector(getSelectedTagIds)

  // the selected tag objects (active-only, what the editor can render)
  const defaultAccountSelectedTags: SelectedTagsGroup = getActiveSelectedTags(allAccountTags, defaultSelectedTags[TAG_TYPE_ACCOUNT])
  const defaultLocationSelectedTags: SelectedTagsGroup = getActiveSelectedTags(allLocationTags, defaultSelectedTags[TAG_TYPE_LOCATION])
  const defaultServiceSelectedTags: SelectedTagsGroup = getActiveSelectedTags(allServiceTags, defaultSelectedTags[TAG_TYPE_SERVICE])

  // Tags that are still assigned to the entity but have been deactivated in
  // Settings. They are not selectable in the editor; we hold onto them so a
  // save merges them back into the payload instead of silently stripping them
  // from the entity.
  const preservedInactiveIdsByType = buildPreservedInactiveTagIdsByType({
    allAccountTags,
    allLocationTags,
    allServiceTags,
    defaultSelectedTags,
  })

  const onTagsSelectionChange = useCallback(
    (selectedOptions: ReadonlyArray<{ id: string }>, tagType: TagGroupType, parentId: string | null | undefined) => {
      const selectedTagIds = selectedOptions.map(({ id }) => id)
      dispatch(
        selectTagIds({
          tagType,
          selectedTagIds,
          parentId,
        })
      )
    },
    [dispatch]
  )

  const onTagsSelectionCancel = useCallback(() => {
    dispatch(resetSelectedTagIds())
    onClose()
  }, [dispatch, onClose])

  const handleAccountTagsChange = useCallback(
    (e: React.SyntheticEvent, selectedOptions: ReadonlyArray<TagMetadata>) =>
      onTagsSelectionChange(selectedOptions, TAG_TYPE_ACCOUNT, defaultAccountSelectedTags?.parentId),
    [onTagsSelectionChange, defaultAccountSelectedTags?.parentId]
  )

  const handleLocationTagsChange = useCallback(
    (e: React.SyntheticEvent, selectedOptions: ReadonlyArray<TagMetadata>) =>
      onTagsSelectionChange(selectedOptions, TAG_TYPE_LOCATION, defaultLocationSelectedTags?.parentId),
    [onTagsSelectionChange, defaultLocationSelectedTags?.parentId]
  )

  const handleServiceTagsChange = useCallback(
    (e: React.SyntheticEvent, selectedOptions: ReadonlyArray<TagMetadata>) =>
      onTagsSelectionChange(selectedOptions, TAG_TYPE_SERVICE, defaultServiceSelectedTags?.parentId),
    [onTagsSelectionChange, defaultServiceSelectedTags?.parentId]
  )

  const onTagsSelectionSave = useCallback(async () => {
    const enabledTagTypes = getEnabledTypes({ disableAccount, disableLocation, disableService })
    const filteredSelectedTagsByEnabledType = pick(selectedTagsUnsaved, enabledTagTypes)

    // merge any inactive-but-still-assigned tag ids back in so they are not
    // silently dropped on save
    const payloadWithPreservedInactive: UpdateTagsPayload = mergePreservedInactiveIds(
      filteredSelectedTagsByEnabledType,
      preservedInactiveIdsByType
    )

    // keep the dialog open on failure so the selection isn't silently lost
    if (onConfirm) {
      try {
        await onConfirm(payloadWithPreservedInactive)
      } catch (error) {
        handleError(error)
        return
      }
    } else {
      const result = await putUpdateTags({ accountId, ...payloadWithPreservedInactive })
      if ('error' in result) {
        handleError(result.error)
        return
      }
    }
    onSave()
  }, [
    accountId,
    disableAccount,
    disableLocation,
    disableService,
    onConfirm,
    onSave,
    preservedInactiveIdsByType,
    putUpdateTags,
    selectedTagsUnsaved,
  ])

  const onDialogClose = useCallback(
    (e: unknown, reason: 'backdropClick' | 'escapeKeyDown') => {
      // we disable close on backdrop click, must specifically click on action buttons in order to cancel editing
      if (reason === 'backdropClick') {
        return
      }

      onTagsSelectionCancel()
    },
    [onTagsSelectionCancel]
  )

  // if we have default options, store those on the selected options slice per tag type on initial load
  // AutoComplete does not perform onChange updates on initial default options set, so we need to do it here, once on default options load
  useEffect(() => {
    if (open && !isFetching && !isLoading) {
      const defaultsByType: Array<[TagGroupType, ReadonlyArray<{ id: string }>, string | null | undefined]> = [
        [TAG_TYPE_ACCOUNT, defaultAccountSelectedTags?.tags ?? [], defaultAccountSelectedTags?.parentId],
        [TAG_TYPE_LOCATION, defaultLocationSelectedTags?.tags ?? [], defaultLocationSelectedTags?.parentId],
        [TAG_TYPE_SERVICE, defaultServiceSelectedTags?.tags ?? [], defaultServiceSelectedTags?.parentId],
      ]
      defaultsByType.forEach(([tagType, selectedTags, parentId]) => {
        onTagsSelectionChange(selectedTags, tagType, parentId)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the default*SelectedTags objects are rebuilt every render; depending on them would re-dispatch (and loop) on each render while open
  }, [open, defaultSelectedTags, isFetching, isLoading])

  useEffect(() => {
    if (open) return undefined
    // cleanup function on unmount, since the parent conditionally renders based on the open status
    return () => {
      onTagsSelectionCancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cleanup keyed to open only; an unstable parent onClose would re-run the reset on every render otherwise
  }, [open])

  return (
    // we only render after the options have been loaded, otherwise the options wont render due to an Autocomplete error for uncontrolled component
    isLoading ? null : (
      <Dialog fullWidth hideBackdrop maxWidth="lg" open={open} onClose={onDialogClose}>
        <DialogTitle sx={{ p: 2 }}>
          <Typography noWrap variant="h4">{`${T.EDIT} tags - ${name}`}</Typography>
          <IconButton
            aria-label="close"
            size="small"
            sx={{
              position: 'absolute',
              right: theme => theme.spacing(1),
              top: theme => theme.spacing(1.5),
            }}
            onClick={onTagsSelectionCancel}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1, px: 5 }}>
            {!disableAccount && (
              <TagsSelect
                label={`${T.ACCOUNT} ${T.TAGS}`}
                selectedTags={defaultAccountSelectedTags?.tags}
                tags={allAccountTags}
                onChange={handleAccountTagsChange}
              />
            )}
            {!disableLocation && (
              <TagsSelect
                label={`${T.LOCATION} ${T.TAGS}`}
                selectedTags={defaultLocationSelectedTags?.tags}
                tags={allLocationTags}
                onChange={handleLocationTagsChange}
              />
            )}
            {!disableService && (
              <TagsSelect
                label={`${T.SERVICE} ${T.TAGS}`}
                selectedTags={defaultServiceSelectedTags?.tags}
                tags={allServiceTags}
                onChange={handleServiceTagsChange}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 1 }}>
          <CancelButton onClick={onTagsSelectionCancel} />
          <Button
            color="primary"
            loading={isUpdateTagsLoading}
            size="small"
            variant="contained"
            {...SaveButtonProps}
            onClick={onTagsSelectionSave}
          >
            {T.SAVE}
          </Button>
        </DialogActions>
      </Dialog>
    )
  )
}

export default EditTagsPopup
